import type { TtqSession, TtqSessionParticleEntry } from '../../services/ttqService';
import {
  buildTtqBirdsEyeColumns,
  listTtqBirdsEyeConvergeMemberIds,
  listTtqBirdsEyeRelationEdges,
  placeTtqBirdsEyeCells,
  TTQ_BIRDS_EYE_CONVERGE_CARD_W,
} from './ttqibBirdsEyeLayout';
import { collectCttqConvergeParticleIds } from './ttqibParticleNumbers';

/** 状态 A：衍生/原始标号节点（再加大，便于点选与 hover 详情） */
export const TTQ_NUMBER_NODE_W = 168;
export const TTQ_NUMBER_NODE_H = 128;

/** 与一对多整卡同宽，加载完整 TtqParticleCard */
export const TTQ_CONVERGE_CARD_W = TTQ_BIRDS_EYE_CONVERGE_CARD_W;
/** 布局占位高度；实际高度由 onMeasureHeight 回写 */
export const TTQ_CONVERGE_CARD_H = 360;
export const TTQ_CONVERGE_CARD_GAP = 48;
/** 成员小卡底边 → 收敛卡顶边的最小间距（再乘 screenBoost） */
export const TTQ_CONVERGE_BAND_GAP = 168;
/** 标号节点之间的最小世界间距（再乘 nodeBoost） */
export const TTQ_NUMBER_NODE_GAP = 20;

export type TtqWorldRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * 从矩形内点 (x1,y1) 沿朝向 (x2,y2) 的射线，与 AABB 边界的交点（出边）。
 * 用于多对一连线在成员小卡边框上的锚点（小红点）。
 */
export function ttqRayAabbExitPoint(
  rect: TtqWorldRect,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { x: number; y: number } | null {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return null;

  const candidates: number[] = [];
  if (Math.abs(dx) >= 1e-9) {
    candidates.push(dx > 0
      ? (rect.x + rect.width - x1) / dx
      : (rect.x - x1) / dx);
  }
  if (Math.abs(dy) >= 1e-9) {
    candidates.push(dy > 0
      ? (rect.y + rect.height - y1) / dy
      : (rect.y - y1) / dy);
  }
  let bestT = Infinity;
  for (const t of candidates) {
    if (t > 1e-6 && t < bestT) bestT = t;
  }
  if (!Number.isFinite(bestT)) return null;
  return { x: x1 + dx * bestT, y: y1 + dy * bestT };
}

export type TtqNumberNodePlacement = {
  particleId: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type TtqConvergeLodPlacement = {
  particleId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  entry: TtqSessionParticleEntry;
  memberIds: string[];
};

export type PlaceTtqConvergeLodOptions = {
  /**
   * 关系层 CSS `scale` 补偿（`ttqConvergeLodScreenBoost`）。
   * 间距按视觉尺寸计算，避免放大后互相遮挡。
   */
  screenBoost?: number;
  /** 实测高度（未乘 boost）；缺省用占位高度 */
  measuredHeights?: Record<string, number>;
  /**
   * 关系层标号节点视觉矩形（已含 nodeBoost）。
   * 收敛卡须避开这些障碍，避免盖住衍生/原始小卡。
   */
  numberNodeObstacles?: readonly TtqWorldRect[];
  /**
   * 标号节点最终落位（含互避后的坐标）。
   * 收敛卡初始锚点应对齐这些位置，而不是一对多整卡矩形。
   */
  numberNodePlacements?: readonly TtqNumberNodePlacement[];
  /** 与渲染侧一致的标号节点放大；用于成员簇初始 y */
  numberNodeBoost?: number;
};

/** 与渲染侧一致的标号节点可读放大（并入逻辑宽高，不再二次 scale） */
export function ttqNumberNodeLodBoost(zoom: number): number {
  return Math.min(2.8, Math.max(1.35, 1.15 / Math.max(zoom, 0.2)));
}

/**
 * 标号节点世界坐标左上角。
 * 逻辑尺寸已含 boost，画布只再 scale(zoom)，与一对多整卡同一套定位，中心对齐。
 */
export function ttqNumberNodeOrigin(
  cardRect: TtqWorldRect,
  nodeBoost = 1,
): { x: number; y: number; width: number; height: number } {
  const boost = Math.max(1, nodeBoost);
  const width = TTQ_NUMBER_NODE_W * boost;
  const height = TTQ_NUMBER_NODE_H * boost;
  return {
    x: cardRect.x + cardRect.width / 2 - width / 2,
    y: cardRect.y + cardRect.height / 2 - height / 2,
    width,
    height,
  };
}

/** 整卡中心的标号节点视觉 AABB（逻辑尺寸已含 boost） */
export function ttqNumberNodeVisualRect(
  cardRect: TtqWorldRect,
  nodeBoost: number,
): TtqWorldRect {
  const origin = ttqNumberNodeOrigin(cardRect, nodeBoost);
  return {
    x: origin.x,
    y: origin.y,
    width: origin.width,
    height: origin.height,
  };
}

/**
 * 为衍生/原始标号节点生成不重叠的位置：优先锚在整卡中心，碰撞时右移/下移。
 */
export function placeTtqNumberNodeAnchors(
  cardRectById: Map<string, TtqWorldRect>,
  particleIds: readonly string[],
  nodeBoost = 1,
): TtqNumberNodePlacement[] {
  const boost = Math.max(1, nodeBoost);
  const gap = TTQ_NUMBER_NODE_GAP * boost;
  const raw: TtqNumberNodePlacement[] = [];
  for (const particleId of particleIds) {
    const rect = cardRectById.get(particleId);
    if (!rect) continue;
    const origin = ttqNumberNodeOrigin(rect, boost);
    raw.push({
      particleId,
      x: origin.x,
      y: origin.y,
      width: origin.width,
      height: origin.height,
    });
  }
  if (raw.length <= 1) return raw;

  const items = raw
    .map((item) => ({ ...item }))
    .sort((a, b) => a.y - b.y || a.x - b.x || a.particleId.localeCompare(b.particleId));

  const placed: TtqNumberNodePlacement[] = [];
  for (const item of items) {
    let guard = 0;
    while (guard < 96) {
      guard += 1;
      const selfBox = {
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
      };
      let blocker: TtqNumberNodePlacement | null = null;
      for (const other of placed) {
        if (aabbOverlap(selfBox, other, gap)) {
          blocker = other;
          break;
        }
      }
      if (!blocker) break;
      const pushRightX = blocker.x + blocker.width + gap;
      const pushDownY = blocker.y + blocker.height + gap;
      const preferRight = (pushRightX - item.x) <= (pushDownY - item.y) * 1.15;
      if (preferRight) {
        item.x = pushRightX;
      } else {
        item.y = pushDownY;
      }
    }
    placed.push(item);
  }
  return placed;
}

function visualSize(
  item: Pick<TtqConvergeLodPlacement, 'particleId' | 'width' | 'height'>,
  boost: number,
  measuredHeights?: Record<string, number>,
) {
  const baseH = measuredHeights?.[item.particleId] ?? item.height ?? TTQ_CONVERGE_CARD_H;
  return {
    width: item.width * boost,
    height: baseH * boost,
  };
}

function aabbOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
  gap: number,
): boolean {
  return a.x < b.x + b.width + gap
    && a.x + a.width + gap > b.x
    && a.y < b.y + b.height + gap
    && a.y + a.height + gap > b.y;
}

type BlockerHit = {
  box: { x: number; y: number; width: number; height: number };
  /** 标号节点：优先下移；其它收敛卡：优先右移 */
  preferDown: boolean;
};

/**
 * 按视觉 AABB 避让：只移动收敛卡，绝不挪动标号小卡。
 * 标号障碍用更大的 band 间距；收敛卡之间用 card gap。
 */
export function resolveTtqConvergeLodOverlaps(
  placements: TtqConvergeLodPlacement[],
  options?: PlaceTtqConvergeLodOptions,
): TtqConvergeLodPlacement[] {
  if (placements.length === 0) return [];

  const boost = Math.max(1, options?.screenBoost ?? 1);
  const peerGap = TTQ_CONVERGE_CARD_GAP * boost;
  const nodeGap = TTQ_CONVERGE_BAND_GAP * boost;
  const measuredHeights = options?.measuredHeights;
  const obstacles = options?.numberNodeObstacles ?? [];

  const items = placements
    .map((item) => ({ ...item }))
    .sort((a, b) => a.x - b.x || a.y - b.y || a.particleId.localeCompare(b.particleId));

  const placed: TtqConvergeLodPlacement[] = [];
  for (const item of items) {
    let guard = 0;
    while (guard < 96) {
      guard += 1;
      const self = visualSize(item, boost, measuredHeights);
      const selfBox = { x: item.x, y: item.y, ...self };

      let hit: BlockerHit | null = null;
      let hitGap = peerGap;
      for (const other of placed) {
        const otherBox = {
          x: other.x,
          y: other.y,
          ...visualSize(other, boost, measuredHeights),
        };
        if (aabbOverlap(selfBox, otherBox, peerGap)) {
          hit = { box: otherBox, preferDown: false };
          hitGap = peerGap;
          break;
        }
      }
      if (!hit) {
        for (const obstacle of obstacles) {
          if (aabbOverlap(selfBox, obstacle, nodeGap)) {
            hit = { box: obstacle, preferDown: true };
            hitGap = nodeGap;
            break;
          }
        }
      }
      if (!hit) break;

      const pushRightX = hit.box.x + hit.box.width + hitGap;
      const pushDownY = hit.box.y + hit.box.height + hitGap;
      if (hit.preferDown) {
        // 标号障碍：优先沉到其下方，必要时再右移（小卡位置保持不动）
        if (item.y < pushDownY) {
          item.y = pushDownY;
        } else {
          item.x = Math.max(item.x, pushRightX);
        }
      } else {
        const preferRight = (pushRightX - item.x) <= (pushDownY - item.y) * 1.25;
        if (preferRight) {
          item.x = pushRightX;
        } else {
          item.y = pushDownY;
        }
      }
    }
    placed.push(item);
  }

  return placed;
}

/**
 * 在一对多卡坐标系下为收敛产物计算固定位置：
 * 成员簇水平居中（按视觉宽度）、下方保留 band 间距；只移动收敛卡避开障碍。
 */
export function placeTtqConvergeLodAnchors(
  session: TtqSession,
  cardRectById: Map<string, TtqWorldRect>,
  options?: PlaceTtqConvergeLodOptions,
): TtqConvergeLodPlacement[] {
  const convergeIds = [...collectCttqConvergeParticleIds(session)];
  if (convergeIds.length === 0) return [];

  const boost = Math.max(1, options?.screenBoost ?? 1);
  const bandGap = TTQ_CONVERGE_BAND_GAP * boost;
  const visualW = TTQ_CONVERGE_CARD_W * boost;
  const nodeBoost = Math.max(1, options?.numberNodeBoost ?? 1);
  const placedNodeById = new Map(
    (options?.numberNodePlacements ?? []).map((item) => [item.particleId, item] as const),
  );
  const entryById = new Map(
    (session.particles ?? []).map((entry) => [entry.particleId, entry] as const),
  );

  const raw: TtqConvergeLodPlacement[] = [];
  for (const particleId of convergeIds) {
    const entry = entryById.get(particleId);
    if (!entry) continue;

    const memberIds = listTtqBirdsEyeConvergeMemberIds(session, particleId);
    const memberRects = memberIds
      .map((id) => {
        const placed = placedNodeById.get(id);
        if (placed) {
          return {
            x: placed.x,
            y: placed.y,
            width: placed.width,
            height: placed.height,
          };
        }
        const rect = cardRectById.get(id);
        return rect ? ttqNumberNodeVisualRect(rect, nodeBoost) : null;
      })
      .filter((rect): rect is TtqWorldRect => Boolean(rect));

    let x: number;
    let y: number;
    if (memberRects.length > 0) {
      const minX = Math.min(...memberRects.map((r) => r.x));
      const maxX = Math.max(...memberRects.map((r) => r.x + r.width));
      const maxY = Math.max(...memberRects.map((r) => r.y + r.height));
      // transformOrigin: top left + scale(boost) → 视觉中心对齐成员包围盒中心
      x = (minX + maxX) / 2 - visualW / 2;
      y = maxY + bandGap;
    } else {
      x = 80 + raw.length * (visualW + TTQ_CONVERGE_CARD_GAP * boost);
      y = 120;
    }

    raw.push({
      particleId,
      x,
      y,
      width: TTQ_CONVERGE_CARD_W,
      height: TTQ_CONVERGE_CARD_H,
      entry,
      memberIds,
    });
  }

  return resolveTtqConvergeLodOverlaps(raw, options);
}

export function listTtqConvergeLodEdges(session: TtqSession) {
  return listTtqBirdsEyeRelationEdges(session);
}

/**
 * 多对一关系层：小卡按源列网格排布（对齐一对多列结构），位置稳定、不被收敛卡挤乱。
 */
export function placeTtqBirdsEyeNumberNodes(
  session: TtqSession,
  nodeBoost = 1,
): TtqNumberNodePlacement[] {
  const boost = Math.max(1, nodeBoost);
  const cellW = TTQ_NUMBER_NODE_W * boost;
  const cellH = TTQ_NUMBER_NODE_H * boost;
  const columns = buildTtqBirdsEyeColumns(session);
  const { cells } = placeTtqBirdsEyeCells(columns, {
    cellW,
    cellH,
    colGap: Math.round(36 * Math.min(boost, 2)),
    rowGap: Math.round(28 * Math.min(boost, 2)),
    pad: 64,
    convergeCardW: 1,
    convergeCardH: 1,
    convergeCardGap: 0,
    convergeBandGap: 0,
  });
  return cells
    .filter((cell) => cell.role !== 'converge')
    .map((cell) => ({
      particleId: cell.particleId,
      x: cell.x,
      y: cell.y,
      width: cell.width ?? cellW,
      height: cell.height ?? cellH,
    }));
}
